import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import LabelledInput from 'components/LabelledInput';
import AppleAuthButton from 'components/AppleAuthButton';
import GoogleAuthButton from 'components/GoogleAuthButton';
import { connect } from 'react-redux';
import Modal from 'components/Modal';
import Button from 'widgets/Button';
import colors from 'assets/styles/jsVariables/colors';
import AuthFooter from 'components/Auth/AuthFooter';
import Loader from 'components/Loader';
import ErrorBar from 'components/ErrorBar';
import {
  signupPartnerAuth, signinPartnerAuthRecurring, signinPartnerAuth,
  activatePartner, socialAuthorization
} from 'redux/actions/activatePartner';
import { getActivationCodeFromUrl, getGoogleAuthCbParam, getValuesFromUrl } from 'libs/auth';
import { Activation, Verification, RetryLogin } from 'components/ModalContent';
import Divider from 'widgets/Divider';
import Icon from 'components/Icon';
import { initRecurring } from 'libs/activate';
import {
  USER_INFO_KEYS, AUTH_FORM_SIGNUP_ERROR_CONFIG,
  ERROR_INFO_KEYS, checkErrors, AUTH_FORM_LOGIN_ERROR_CONFIG,
  GOOGLE_AUTH_CONTINUE_ERROR_CONFIG
} from '../config';
import { AUTH0_CONNECTIONS } from 'components/Auth/config'
import styles from './style';
import './Auth.scss';

class Auth extends React.Component {
  constructor(props) {
    super(props);
    const urlValues = getValuesFromUrl(['email']);
    this.state = {
      isSignup: true,
      emailVerifyModalOpen: false,
      isRecurrLoginRetry: false,
      userInfo: {
        [USER_INFO_KEYS.FIRST_NAME]: '',
        [USER_INFO_KEYS.LAST_NAME]: '',
        [USER_INFO_KEYS.EMAIL]: urlValues.email,
        [USER_INFO_KEYS.PASSWORD]: '',
        [USER_INFO_KEYS.ACTIVATION_CODE]: getActivationCodeFromUrl(),
      },
      errorInfo: {
        [ERROR_INFO_KEYS.AUTH_FORM]: {},
        errorConfigAuth: AUTH_FORM_SIGNUP_ERROR_CONFIG,
      },
      errorBar: {
        show: false,
        message: ''
      }
    };

    const params = getGoogleAuthCbParam();
    if (params) {
      const {
        access_token, userInfo, error_description
      } = params;
      if (error_description) {
        this.showErrorBar({ message: error_description });
      } else {
        this.state.userInfo = userInfo;
        setImmediate(() => {
          this.showEmailVerifyModal();
          props.activatePartner(
            { accessToken: access_token },
            userInfo,
            this.handleSaveCallback
          );
        });
      }
    }
  }

  setRecurrLoginRetry = () => {
    this.setState({ isRecurrLoginRetry: true });
  }
  resetRecurrLoginRetry = () => {
    this.setState({ isRecurrLoginRetry: false });
  }

  validateFields = (config = {}) => {
    this.handleErrorBarClose();

    const { userInfo, errorInfo } = this.state;
    const { errorFields } = config;

    const errors = checkErrors(errorFields, userInfo);
    errorInfo[config.errorKey] = errors;

    this.setState({ errorInfo });
    return errorInfo;
  }

  toggleView = () => {
    const { isSignup, errorInfo } = this.state;
    errorInfo.errorConfigAuth = !isSignup ?
      AUTH_FORM_SIGNUP_ERROR_CONFIG :
      AUTH_FORM_LOGIN_ERROR_CONFIG;
    errorInfo[ERROR_INFO_KEYS.AUTH_FORM] = {};

    this.setState({ isSignup: !isSignup, errorInfo });
  }

  handleSocialAuthorisationContinue = (connection) => {
    this.isAuthFormDirty = true;
    const errorInfoAuthForm = this.validateFields(GOOGLE_AUTH_CONTINUE_ERROR_CONFIG);

    if (Object.keys(errorInfoAuthForm[ERROR_INFO_KEYS.AUTH_FORM]).length) {
      return;
    }

    const { userInfo } = this.state;

    this.props.socialAuthorization(connection, userInfo);
  }

  handleAuthFormSave = () => {
    const { errorInfo: { errorConfigAuth } } = this.state;
    const errorInfo = this.validateFields(errorConfigAuth);

    this.isAuthFormDirty = true;
    if (Object.keys(errorInfo[ERROR_INFO_KEYS.AUTH_FORM]).length) {
      return;
    }

    const { userInfo, isSignup } = this.state;

    if (isSignup) {
      this.props.signupPartnerAuth(userInfo, this.handleSaveCallback);
    } else {
      this.props.signinPartnerAuth(userInfo, this.handleSaveCallback);
    }
  }

  handleSaveCallback = ({
    recurringLogin, message, closeResetModal,
    closeEmailVerifyModal, showEmailVerifyModal
  }) => {
    const { userInfo } = this.state;
    if (recurringLogin) {
      initRecurring({
        initCb: this.recurringInitCb,
        endCb: this.recurringEndCb,
        api: this.props.signinPartnerAuthRecurring,
        apiParams: [userInfo],
        successCb: this.handleSaveCallback
      });
    }

    if (message) {
      this.showErrorBar({ message });
    }

    if (closeResetModal) {
      this.closeResetPasswordModal();
    }

    if (closeEmailVerifyModal) {
      this.closeEmailVerifyModal();
    }

    if (showEmailVerifyModal) {
      this.showEmailVerifyModal();
    }
  }

  showErrorBar = ({ message }) => {
    const errorBar = {
      show: true,
      message
    };
    this.setState({ errorBar });
  }

  showEmailVerifyModal = () => {
    this.setState({ emailVerifyModalOpen: true });
  }

  closeEmailVerifyModal = () => {
    this.setState({ emailVerifyModalOpen: false });
  }

  handleErrorBarClose = () => {
    const { errorBar } = this.state;
    if (errorBar.show) {
      const _errorBar = {
        show: false
      };
      this.setState({ errorBar: _errorBar });
    }
  }

  recurringInitCb = () => {
    this.showEmailVerifyModal();
  }

  recurringEndCb = () => {
    this.setRecurrLoginRetry();
  }

  handleChange = (e, cb) => {
    const { userInfo } = this.state;
    userInfo[e.target.name] = e.target.value;
    this.setState({ userInfo }, cb);
  }

  handleAuthFormInputChange = e => this.handleChange(e, () => {
    if (this.isAuthFormDirty) {
      const { errorInfo: { errorConfigAuth } } = this.state;
      this.validateFields(errorConfigAuth);
    }
  });

  handleRetryRecurrLogin = () => {
    const { userInfo } = this.state;
    this.resetRecurrLoginRetry();
    initRecurring({
      initCb: this.recurringInitCb,
      api: this.props.signinPartnerAuthRecurring,
      endCb: this.recurringEndCb,
      apiParams: [userInfo],
      successCb: this.handleSaveCallback,
      isActivateStraightAway: true
    });
  }

  render() {
    const {
      userInfo,
      errorInfo,
      emailVerifyModalOpen,
      errorBar, isRecurrLoginRetry,
      isSignup
    } = this.state;
    const { partnerActivate, intl } = this.props;

    if (partnerActivate.isProcessingAuth) {
      return (
        <div className="Activate__Partner">
          <Loader />
        </div>
      );
    }

    const errorInfoAuthForm = errorInfo[ERROR_INFO_KEYS.AUTH_FORM] || {};

    let EmailVerifyTitle = partnerActivate.isActivatingPartner
      ? <FormattedMessage id="components.activatePartner.auth.activation.title" />
      : <FormattedMessage id="components.activatePartner.auth.verification.title" />;
    let EmailVerifyContent = partnerActivate.isActivatingPartner ? Activation : Verification;

    if (isRecurrLoginRetry) {
      EmailVerifyTitle = (
        <span
          style={{ fontFamily: 'museosansrounded_500', color: colors.darkerGrey, fontSize: '22px' }}
        >
          <FormattedMessage id="components.activatePartner.auth.retryLogin.title" />
        </span>
      );

      EmailVerifyContent = () => (
        <RetryLogin
          onClick={this.handleRetryRecurrLogin}
          rootStyleButton={styles.AuthButtonStyle}
          labelStyleButton={styles.AuthButtonLabelStyle}
        />
      );
    }

    return (
      <div>
        <ErrorBar errorBar={errorBar} onClose={this.handleErrorBarClose} />
        <picture>
          <source media="(min-width: 1023px)" srcSet="/images/sub_activate_img_desktop.png" />
          <source media="(min-width: 767px)" srcSet="/images/sub_activate_Img_tab.png" />
          <img src="/images/sub_activate_img_mob.png" alt="activate" className="Activate__Partner__Auth__BannerImage" width="100%" />
        </picture>
        <div className="Activate__Partner__Auth">
          <Modal
            title={EmailVerifyTitle}
            showCross={false}
            open={emailVerifyModalOpen}
            {...styles.modalStyleVerifyEmail}
            className="Activate__Partner__Auth__VerifyModal"
          >
            <div className="Activate__Partner__Auth__Modal__Content">
              <EmailVerifyContent
                themeColor={{
                  primary: colors.partnerThemePrimary
                }}
              />
            </div>
          </Modal>
          <div className="Activate__Partner__Auth__FormCardWrap">
            <div className="Activate__Partner__Auth__FormCard">
              <div className="Activate__Partner__Auth__FormCard__Logo">
                <img src="/images/logo/MuseMSLogo.png" className="Activate_Partner_Logo-Desktop" alt="activate" width="338px" />
                <img src="/images/logo/MuseMSLogo.png" className="Activate_Partner_Logo-MobileTab" alt="activate" width="254px" />
              </div>
              <div className="Activate__Partner__Auth__Description">
                <FormattedMessage id="components.activatePartner.auth.formCard.description" />
              </div>
              <React.Fragment>
                <div className="Activate__Partner__Auth__InputWrap">
                  <LabelledInput
                    iconName="key"
                    placeholder={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.activationCode.placeholder' })}
                    labelText={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.activationCode.label' })}
                    name={USER_INFO_KEYS.ACTIVATION_CODE}
                    value={userInfo[USER_INFO_KEYS.ACTIVATION_CODE]}
                    onChange={this.handleAuthFormInputChange}
                    onEnter={this.handleAuthFormSave}
                    errorText={errorInfoAuthForm[USER_INFO_KEYS.ACTIVATION_CODE] &&
                      intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.ACTIVATION_CODE] })}
                  />
                </div>
              </React.Fragment>

              <div className="Auth__FormCard__Action">
                <div className="Auth__FormCard__Action__SocialAuth">
                  <GoogleAuthButton
                    onClick={() => this.handleSocialAuthorisationContinue(AUTH0_CONNECTIONS.google)}
                    disabled={partnerActivate.isProcessingAuth}
                    isSignup={isSignup}
                  />
                </div>
                <div className="Auth__FormCard__Action__SocialAuth">
                  <AppleAuthButton
                    onClick={() => this.handleSocialAuthorisationContinue(AUTH0_CONNECTIONS.apple)}
                    disabled={partnerActivate.isProcessingAuth}
                    isSignup={isSignup}
                  />
                </div>
              </div>
              <div className="Activate__Partner__Auth__FormCard__Action__Separator">
                <Divider
                  style={{
                    height: '1px',
                    color: colors.lightestGrey,
                    margin: '25px'
                  }}
                />
                <span className="Activate__Partner__Auth__FormCard__Action__Separator__Text">
                  <FormattedMessage id="components.activatePartner.auth.formCard.separatorText" />
                </span>
              </div>

              <div className="Partner__Auth__FormCard__TabWrap">
                <div
                  role="button"
                  tabIndex="0"
                  onClick={this.toggleView}
                  className={`Partner__Auth__FormCard__TabWrap__Tab--Left ${(isSignup && 'selected') || ''}`}
                >
                  {
                    !!isSignup &&
                    <Icon
                      className="Tabicon"
                      width="14px"
                      height="14px"
                      name="check-circle"
                      fill="white"
                      alt="icon"
                    />
                  }
                  <FormattedMessage id="components.activatePartner.auth.formCard.tab.signUpLabel">
                    {message => <span className="Partner__Auth__FormCard__TabWrap__Tab__Text">{message}</span>}
                  </FormattedMessage>
                </div>
                <div
                  role="button"
                  tabIndex="0"
                  onClick={this.toggleView}
                  className={`Partner__Auth__FormCard__TabWrap__Tab--Right ${(!isSignup && 'selected') || ''}`}
                >
                  {
                    !isSignup &&
                    <Icon
                      className="Tabicon"
                      width="14px"
                      height="14px"
                      name="check-circle"
                      fill="white"
                      alt="icon"
                    />
                  }
                  <FormattedMessage id="components.activatePartner.auth.formCard.tab.signInLabel">
                    {message => <span className="Partner__Auth__FormCard__TabWrap__Tab__Text">{message}</span>}
                  </FormattedMessage>
                </div>
              </div>
              {
                !isSignup &&
                <div className="Partner__Auth__SectionDetails">
                  <span className="icon">
                    <Icon name="warning" width="14px" height="14px" fill={colors.mediumGrey} />
                  </span>
                  <span style={{ verticalAlign: 'text-top' }}>
                    <FormattedHTMLMessage id="components.activatePartner.auth.formCard.action.signIn.sectionDetails" />
                  </span>
                </div>
              }
              {
                isSignup &&
                <React.Fragment>
                  <div className="Activate__Partner__Auth__NameSection">
                    <div className="Activate__Partner__Auth__InputWrap">
                      <LabelledInput
                        iconName="profile"
                        placeholder={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.firstName.placeholder' })}
                        labelText={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.firstName.label' })}
                        value={userInfo[USER_INFO_KEYS.FIRST_NAME]}
                        name={USER_INFO_KEYS.FIRST_NAME}
                        onChange={this.handleAuthFormInputChange}
                        onEnter={this.handleAuthFormSave}
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME] })}
                      />
                    </div>
                    <div className="Activate__Partner__Auth__InputWrap">
                      <LabelledInput
                        placeholder={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.lastName.placeholder' })}
                        name={USER_INFO_KEYS.LAST_NAME}
                        value={userInfo[USER_INFO_KEYS.LAST_NAME]}
                        onEnter={this.handleAuthFormSave}
                        onChange={this.handleAuthFormInputChange}
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME] })}
                      />
                    </div>
                  </div>
                </React.Fragment>
              }
              <React.Fragment>
                <div className="Activate__Partner__Auth__InputWrap">
                  <LabelledInput
                    iconName="email-grey"
                    placeholder={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.email.placeholder' })}
                    labelText={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.email.label' })}
                    name={USER_INFO_KEYS.EMAIL}
                    value={userInfo[USER_INFO_KEYS.EMAIL]}
                    onEnter={this.handleAuthFormSave}
                    onChange={this.handleAuthFormInputChange}
                    errorText={errorInfoAuthForm[USER_INFO_KEYS.EMAIL] &&
                      intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.EMAIL] })}
                    autoFocus
                  />
                </div>
                <div className="Activate__Partner__Auth__InputWrap LabelledInput__Input--Password">
                  <LabelledInput
                    type="password"
                    placeholder={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.password.placeholder' })}
                    labelText={intl.formatMessage({ id: 'components.activatePartner.auth.inputWrap.password.label' })}
                    name={USER_INFO_KEYS.PASSWORD}
                    value={userInfo[USER_INFO_KEYS.PASSWORD]}
                    onEnter={this.handleAuthFormSave}
                    onChange={this.handleAuthFormInputChange}
                    errorText={errorInfoAuthForm[USER_INFO_KEYS.PASSWORD] &&
                      intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.PASSWORD] })}
                    iconName="lock"
                  />
                </div>
              </React.Fragment>
              <div className="Activate__Partner__Auth__FormCard__Action">
                <Button
                  id="authAction"
                  cssStyles={{
                    root: styles.AuthButtonStyle,
                    label: styles.AuthButtonLabelStyle,
                  }}
                  onClick={this.handleAuthFormSave}
                >
                  {
                    isSignup ?
                      <FormattedMessage id="components.activatePartner.auth.formCard.action.signUp.buttonLabel" /> :
                      <FormattedMessage id="components.activatePartner.auth.formCard.action.signIn.buttonLabel" />
                  }
                </Button>
              </div>
            </div>
            <div className="Activate__Partner__Auth__Footer">
              <AuthFooter />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Auth.propTypes = {
  partnerActivate: PropTypes.object.isRequired,
  signupPartnerAuth: PropTypes.func.isRequired,
  signinPartnerAuth: PropTypes.func.isRequired,
  signinPartnerAuthRecurring: PropTypes.func.isRequired,
  socialAuthorization: PropTypes.func.isRequired,
  activatePartner: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired
};

function mapStateToProps({ partnerActivate }) {
  return { partnerActivate };
}

export default connect(
  mapStateToProps,
  {
    signupPartnerAuth,
    signinPartnerAuth,
    signinPartnerAuthRecurring,
    socialAuthorization,
    activatePartner
  }
)(injectIntl(Auth));



// WEBPACK FOOTER //
// ./src/views/ActivatePartner/Auth/Auth.jsx